import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@evertrue/et-components';

const TABS_PER_PAGE = 5;

const AddressTabs = ({ addresses, activeIndex, onTabClick }) => {
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(addresses.length / TABS_PER_PAGE);
  const startIndex = currentPage * TABS_PER_PAGE;
  const endIndex = Math.min(startIndex + TABS_PER_PAGE, addresses.length);

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      // When navigating to previous page, select the first tab of that page
      const newActiveIndex = (currentPage - 1) * TABS_PER_PAGE;
      onTabClick(newActiveIndex);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      // When navigating to next page, select the first tab of that page
      const newActiveIndex = (currentPage + 1) * TABS_PER_PAGE;
      onTabClick(newActiveIndex);
    }
  };

  const handleTabClick = (index) => {
    onTabClick(index);
  };

  // If only one address, show as heading instead of tab
  if (addresses.length === 1) {
    return (
      <div className="address-tabs">
        <div className="address-tabs--heading">Address 1</div>
      </div>
    );
  }

  // If 5 or fewer addresses, no pagination needed
  if (addresses.length <= TABS_PER_PAGE) {
    return (
      <div className="address-tabs">
        <div className="address-tabs--list">
          {addresses.map((address, index) => (
            <button
              key={address.id || index}
              className={classNames('address-tabs--tab', {
                'address-tabs--tab-active': index === activeIndex,
              })}
              onClick={() => handleTabClick(index)}
              type="button"
            >
              ADDRESS {index + 1}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Pagination required for 6+ addresses
  return (
    <div className="address-tabs">
      <div className="address-tabs--container">
        {currentPage > 0 && (
          <button
            className="address-tabs--arrow address-tabs--arrow-left"
            onClick={handlePrevPage}
            type="button"
            aria-label="Previous page"
          >
            <Icon icon="chevron-left" size={1} />
          </button>
        )}

        <div className="address-tabs--list">
          {addresses.slice(startIndex, endIndex).map((address, relativeIndex) => {
            const absoluteIndex = startIndex + relativeIndex;
            return (
              <button
                key={address.id || absoluteIndex}
                className={classNames('address-tabs--tab', {
                  'address-tabs--tab-active': absoluteIndex === activeIndex,
                })}
                onClick={() => handleTabClick(absoluteIndex)}
                type="button"
              >
                ADDRESS {absoluteIndex + 1}
              </button>
            );
          })}
        </div>

        {currentPage < totalPages - 1 && (
          <button
            className="address-tabs--arrow address-tabs--arrow-right"
            onClick={handleNextPage}
            type="button"
            aria-label="Next page"
          >
            <Icon icon="chevron-right" size={1} />
          </button>
        )}
      </div>
    </div>
  );
};

AddressTabs.propTypes = {
  addresses: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeIndex: PropTypes.number.isRequired,
  onTabClick: PropTypes.func.isRequired,
};

export default AddressTabs;
